import { useState, useRef } from "react";
import BotonMapaProceso from "./BotonMapaProceso";
import { subirDatos } from "./syncSupabase";
// ─── CONSTANTES ───────────────────────────────────────────────
const LOG = {
  info: (msg, data) => console.log(`[IMPORTADOR][INFO] ${msg}`, data || ''),
  error: (msg, err) => console.error(`[IMPORTADOR][ERROR] ${msg}`, err || ''),
};
const storage = {
  get: (key) => { try { return JSON.parse(localStorage.getItem(key) || '[]'); } catch (e) { return []; } },
  set: (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); return true; } catch (e) { return false; } },
};
// ─── CONFIGURACIÓN DE MÓDULOS ─────────────────────────────────
const MODULOS_CONFIG = {
  fsc: {
    label: "🌿 FSC Arqueo",
    storage: "alqos_fsc_arqueo",
    color: "#22c55e",
    campos: [
      { key: "fecha", label: "Fecha", tipo: "fecha", requerido: true, ejemplo: "2026-04-01" },
      { key: "tipoMovimiento", label: "Tipo (ENTRADA/SALIDA/SCRAP)", tipo: "enum", valores: ["ENTRADA", "SALIDA", "SCRAP"], requerido: true, ejemplo: "ENTRADA" },
      { key: "producto", label: "Producto", tipo: "texto", requerido: true, ejemplo: "Cartulina 350g FSC" },
      { key: "descripcion", label: "Descripción", tipo: "texto", requerido: false, ejemplo: "Compra proveedor X" },
      { key: "certificadoFSC", label: "Certificado FSC", tipo: "enum", valores: ["FSC 100%", "FSC Mix Credit", "FSC Mix X%", "FSC Recycled", "Controlled Wood"], requerido: true, ejemplo: "FSC 100%" },
      { key: "origen", label: "Origen", tipo: "enum", valores: ["Compra", "Producción", "Scrap proceso", "Ajuste inventario", "Devolución", "Otro"], requerido: false, ejemplo: "Compra" },
      { key: "kgEntrada", label: "Kg Entrada", tipo: "numero", requerido: false, ejemplo: "500" },
      { key: "kgSalida", label: "Kg Salida", tipo: "numero", requerido: false, ejemplo: "0" },
      { key: "kgScrap", label: "Kg Scrap", tipo: "numero", requerido: false, ejemplo: "0" },
      { key: "proveedor", label: "Proveedor", tipo: "texto", requerido: false, ejemplo: "Papelera SA" },
      { key: "ot", label: "OT Vinculada", tipo: "texto", requerido: false, ejemplo: "OT-2026-001" },
      { key: "observaciones", label: "Observaciones", tipo: "texto", requerido: false, ejemplo: "Material certificado lote 22" },
    ],
    generarId: (lista) => {
      const anio = new Date().getFullYear();
      return `FSC-${anio}-${String(lista.length + 1).padStart(4, "0")}`;
    },
    duplicadoKey: (r) => `${r.fecha}_${r.producto}_${r.tipoMovimiento}`,
  },
  // ── NC — parcheado 2026-09-01: enums alineados con LISTAS_DEFAULT de
  // NoConformidades.js (antes usaba valores propios que no existían en el
  // módulo real y rechazaba cualquier carga válida), se agregaron estado/
  // fechaCierre/cerradoPor (antes toda NC importada quedaba sin estado y
  // Escritorio de Calidad la contaba como "abierta +7 días" sin importar si
  // en el Excel original ya estaba cerrada), y se sumaron ~20 campos que el
  // módulo real sí guarda y antes se perdían en la importación.
  nc: {
    label: "⚠️ No Conformidades",
    storage: "alqos_ncs",
    color: "#e63946",
    campos: [
      { key: "fecha", label: "Fecha y Hora", tipo: "fecha", requerido: true, ejemplo: "2026-04-01T10:00" },
      { key: "rip", label: "RIP", tipo: "texto", requerido: false, ejemplo: "101" },
      { key: "proceso", label: "Proceso", tipo: "texto", requerido: true, ejemplo: "Impresión Offset" },
      { key: "sector", label: "Sector", tipo: "texto", requerido: false, ejemplo: "Impresión" },
      { key: "trabajo", label: "Trabajo / Producto", tipo: "texto", requerido: false, ejemplo: "Caja plegadiza" },
      { key: "codigoProducto", label: "Código de Producto", tipo: "texto", requerido: false, ejemplo: "COD-001" },
      { key: "cliente", label: "Cliente", tipo: "texto", requerido: false, ejemplo: "Cliente ABC" },
      { key: "op", label: "OP", tipo: "texto", requerido: false, ejemplo: "OP-2026-001" },
      { key: "origen", label: "Origen", tipo: "texto", requerido: true, ejemplo: "Producción" },
      { key: "deteccion", label: "Detección", tipo: "texto", requerido: false, ejemplo: "CONTROL DE CALIDAD" },
      { key: "tipoNC", label: "Tipo NC", tipo: "texto", requerido: true, ejemplo: "INTERNA" },
      { key: "severidad", label: "Severidad", tipo: "enum", valores: ["critica", "mayor", "menor"], requerido: true, ejemplo: "mayor" },
      { key: "escalamiento", label: "Escalamiento", tipo: "enum", valores: ["bajo", "medio", "alto"], requerido: false, ejemplo: "bajo" },
      { key: "descripcion", label: "Descripción", tipo: "texto", requerido: true, ejemplo: "Desvío en registro de color" },
      { key: "motivo", label: "Motivo", tipo: "texto", requerido: false, ejemplo: "Falla de registro" },
      { key: "disposicion", label: "Disposición", tipo: "texto", requerido: false, ejemplo: "Reprocesar" },
      { key: "cantidad", label: "Cantidad Afectada", tipo: "numero", requerido: false, ejemplo: "1000" },
      { key: "unidad", label: "Unidad", tipo: "texto", requerido: false, ejemplo: "unidad" },
      { key: "cantidadProduccion", label: "Cantidad Producción", tipo: "numero", requerido: false, ejemplo: "1000" },
      { key: "causaRaiz", label: "Causa Raíz", tipo: "texto", requerido: false, ejemplo: "Falta de calibración" },
      { key: "accionInmediata", label: "Acción Inmediata", tipo: "texto", requerido: false, ejemplo: "Se detuvo la máquina" },
      { key: "accionCorrectiva", label: "Acción Correctiva", tipo: "texto", requerido: false, ejemplo: "Recalibrar máquina" },
      { key: "accionPreventiva", label: "Acción Preventiva", tipo: "texto", requerido: false, ejemplo: "Checklist diario" },
      { key: "responsable", label: "Responsable", tipo: "texto", requerido: false, ejemplo: "Jefe de Producción" },
      { key: "solicitadoPor", label: "Solicitado Por", tipo: "texto", requerido: false, ejemplo: "Jefe de Calidad" },
      { key: "fechaCompromiso", label: "Fecha Compromiso", tipo: "fecha", requerido: false, ejemplo: "2026-05-01" },
      { key: "metodoverificacion", label: "Método de Verificación", tipo: "texto", requerido: false, ejemplo: "Inspección visual" },
      { key: "plazoVerificacion", label: "Plazo de Verificación", tipo: "fecha", requerido: false, ejemplo: "2026-05-15" },
      { key: "verificacionEficacia", label: "Verificación de Eficacia", tipo: "texto", requerido: false, ejemplo: "Sin recurrencia en 30 días" },
      { key: "estado", label: "Estado", tipo: "enum", valores: ["abierta", "en_analisis", "en_proceso", "cerrada"], requerido: false, ejemplo: "cerrada" },
      { key: "fechaCierre", label: "Fecha de Cierre", tipo: "fecha", requerido: false, ejemplo: "2026-04-15" },
      { key: "cerradoPor", label: "Cerrado Por", tipo: "texto", requerido: false, ejemplo: "Jefe de Calidad" },
      { key: "nota", label: "Nota", tipo: "texto", requerido: false, ejemplo: "" },
      { key: "precioUnitario", label: "Precio por Unidad", tipo: "numero", requerido: false, ejemplo: "2.5" },
      { key: "estuRecup", label: "Estudio Recuperable", tipo: "numero", requerido: false, ejemplo: "0" },
      { key: "estuScrap", label: "Estudio Scrap", tipo: "numero", requerido: false, ejemplo: "0" },
    ],
    generarId: (lista) => {
      const anio = new Date().getFullYear();
      return `NCI-${anio}-${String(lista.length + 1).padStart(3, "0")}`;
    },
    duplicadoKey: (r) => `${r.fecha}_${r.proceso}_${r.descripcion?.slice(0, 30)}`,
  },
  ac: {
    label: "🔁 Acciones Correctivas",
    storage: "alqos_acciones_correctivas",
    color: "#7B5BF2",
    campos: [
      { key: "idNC", label: "ID NC vinculada", tipo: "texto", requerido: true, ejemplo: "NCI-2026-001" },
      { key: "tipoAccion", label: "Tipo (correccion/correctiva/preventiva)", tipo: "enum", valores: ["correccion", "correctiva", "preventiva"], requerido: true, ejemplo: "correctiva" },
      { key: "causaRaiz", label: "Causa Raíz", tipo: "texto", requerido: true, ejemplo: "Falta de procedimiento" },
      { key: "descripcionAccion", label: "Descripción de la Acción", tipo: "texto", requerido: true, ejemplo: "Actualizar procedimiento P-001" },
      { key: "responsable", label: "Responsable", tipo: "texto", requerido: true, ejemplo: "Jefe de Calidad" },
      { key: "fechaCompromiso", label: "Fecha Compromiso", tipo: "fecha", requerido: true, ejemplo: "2026-05-15" },
      { key: "alcance", label: "Alcance", tipo: "texto", requerido: false, ejemplo: "Toda la línea de impresión" },
      { key: "estado", label: "Estado", tipo: "enum", valores: ["abierta", "en_proceso", "implementada", "verificada", "cerrada"], requerido: false, ejemplo: "abierta" },
      { key: "evidenciaImplementacion", label: "Evidencia Implementación", tipo: "texto", requerido: false, ejemplo: "Procedimiento actualizado" },
      { key: "evidenciaEficacia", label: "Evidencia Eficacia", tipo: "texto", requerido: false, ejemplo: "Sin recurrencia en 30 días" },
      { key: "resultadoEficacia", label: "Resultado Eficacia", tipo: "enum", valores: ["eficaz", "no_eficaz", "pendiente"], requerido: false, ejemplo: "eficaz" },
    ],
    generarId: (lista) => {
      const anio = new Date().getFullYear();
      return `AC-${anio}-${String(lista.length + 1).padStart(3, "0")}`;
    },
    duplicadoKey: (r) => `${r.idNC}_${r.descripcionAccion?.slice(0, 30)}`,
  },
};
// ─── PARSEO CSV ───────────────────────────────────────────────
function parsearCSV(texto) {
  try {
    const lineas = texto.trim().split("\n").map(l => l.trim()).filter(l => l);
    if (lineas.length < 2) return { headers: [], filas: [] };
    const headers = lineas[0].split(",").map(h => h.replace(/^"|"$/g, "").trim());
    const filas = lineas.slice(1).map(linea => {
      const valores = [];
      let dentro = false, actual = "";
      for (let i = 0; i < linea.length; i++) {
        const c = linea[i];
        if (c === '"') { dentro = !dentro; continue; }
        if (c === "," && !dentro) { valores.push(actual.trim()); actual = ""; continue; }
        actual += c;
      }
      valores.push(actual.trim());
      const obj = {};
      headers.forEach((h, i) => { obj[h] = valores[i] || ""; });
      return obj;
    });
    return { headers, filas };
  } catch (err) {
    LOG.error("Error al parsear CSV", err);
    return { headers: [], filas: [] };
  }
}
// ─── VALIDAR FILA ─────────────────────────────────────────────
function validarFila(fila, campos, index) {
  const errores = [];
  campos.forEach(campo => {
    const val = fila[campo.label] || fila[campo.key] || "";
    if (campo.requerido && !val.trim()) {
      errores.push(`Fila ${index + 2}: "${campo.label}" es requerido`);
      return;
    }
    if (val && campo.tipo === "numero" && isNaN(Number(val))) {
      errores.push(`Fila ${index + 2}: "${campo.label}" debe ser un número`);
    }
    if (val && campo.tipo === "fecha" && isNaN(Date.parse(val))) {
      errores.push(`Fila ${index + 2}: "${campo.label}" tiene formato de fecha inválido`);
    }
    if (val && campo.tipo === "enum" && campo.valores && !campo.valores.includes(val)) {
      errores.push(`Fila ${index + 2}: "${campo.label}" debe ser uno de: ${campo.valores.join(", ")}`);
    }
  });
  return errores;
}
// ─── MAPEAR FILA A ENTIDAD ────────────────────────────────────
function mapearFila(fila, campos, config, existentes, usuario) {
  const obj = { id: Date.now() + Math.random(), _source: "importacion_csv", _schema: "v1" };
  campos.forEach(campo => {
    const val = fila[campo.label] || fila[campo.key] || "";
    if (campo.tipo === "numero") obj[campo.key] = Number(val) || 0;
    else obj[campo.key] = val || (campo.key === "escalamiento" ? "bajo" : campo.key === "estado" ? "abierta" : campo.key === "resultadoEficacia" ? "pendiente" : "");
  });
  obj.creadoPor = usuario?.nombre || "Importación CSV";
  obj.rolCreador = usuario?.rol || "";
  obj.fechaRegistro = new Date().toISOString();
  if (config.generarId) {
    const todos = [...existentes];
    obj[config.storage === "alqos_fsc_arqueo" ? "idMovimiento" : config.storage === "alqos_ncs" ? "nci" : "idAccion"] = config.generarId(todos);
  }
  return obj;
}
// ─── DESCARGAR PLANTILLA ──────────────────────────────────────
function descargarPlantilla(moduloKey) {
  const config = MODULOS_CONFIG[moduloKey];
  const headers = config.campos.map(c => c.label);
  const ejemplos = config.campos.map(c => c.ejemplo || "");
  const csv = [headers.join(","), ejemplos.join(",")].join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `PLANTILLA_${moduloKey.toUpperCase()}_ALQOS.csv`;
  a.click();
  LOG.info("Plantilla descargada", { modulo: moduloKey });
}
// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────
export default function ImportadorCSV({ onVolver, oscuro, usuario }) {
  const [moduloSeleccionado, setModuloSeleccionado] = useState(null);
  const [fase, setFase] = useState("seleccion"); // seleccion | preview | confirmacion | resultado
  const [archivoNombre, setArchivoNombre] = useState("");
  const [filasValidas, setFilasValidas] = useState([]);
  const [filasInvalidas, setFilasInvalidas] = useState([]);
  const [duplicados, setDuplicados] = useState([]);
  const [resultado, setResultado] = useState(null);
  const fileRef = useRef();
  const bg = oscuro ? "#09184D" : "#F0EEFF";
  const cardBg = oscuro ? "#0d2160" : "white";
  const texto = oscuro ? "#EDF2FA" : "#2F3E2E";
  const borde = oscuro ? "#1e3a8a" : "#eee";
  const navBg = oscuro ? "#060f30" : "#09184D";
  const subtexto = oscuro ? "#9aabd4" : "#808085";
  const card = { background: cardBg, borderRadius: "12px", padding: "1.5rem", border: `1px solid ${borde}`, marginBottom: "12px" };
  const btn = (color) => ({ padding: "10px 20px", background: color, color: "white", border: "none", borderRadius: "10px", cursor: "pointer", fontSize: "13px", fontWeight: "600" });
  const config = moduloSeleccionado ? MODULOS_CONFIG[moduloSeleccionado] : null;
  const leerArchivo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setArchivoNombre(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const texto = ev.target.result;
        const { filas } = parsearCSV(texto);
        if (!filas.length) { alert("El archivo está vacío o tiene formato incorrecto."); return; }
        const existentes = storage.get(config.storage);
        const keysExistentes = new Set(existentes.map(e => config.duplicadoKey(e)));
        const validas = [], invalidas = [], dupes = [];
        filas.forEach((fila, i) => {
          const errores = validarFila(fila, config.campos, i);
          if (errores.length > 0) { invalidas.push({ fila, errores, index: i }); return; }
          const key = config.duplicadoKey(
            Object.fromEntries(config.campos.map(c => [c.key, fila[c.label] || fila[c.key] || ""]))
          );
          if (keysExistentes.has(key)) { dupes.push({ fila, index: i }); return; }
          validas.push(fila);
        });
        setFilasValidas(validas);
        setFilasInvalidas(invalidas);
        setDuplicados(dupes);
        setFase("preview");
        LOG.info("Archivo leído", { validas: validas.length, invalidas: invalidas.length, duplicados: dupes.length });
      } catch (err) { LOG.error("Error leyendo archivo", err); alert("Error al leer el archivo."); }
    };
    reader.readAsText(file, "UTF-8");
  };
  const confirmarImportacion = () => {
    try {
      const existentes = storage.get(config.storage);
      const nuevos = filasValidas.map(fila => {
        const filaObj = Object.fromEntries(config.campos.map(c => [c.key, fila[c.label] || fila[c.key] || ""]));
        return mapearFila(filaObj, config.campos, config, existentes, usuario);
      });
      // Recalcular IDs en orden para evitar colisiones
      const todos = [...existentes];
      const conIds = nuevos.map((r, i) => {
        const idKey = config.storage === "alqos_fsc_arqueo" ? "idMovimiento" : config.storage === "alqos_ncs" ? "nci" : "idAccion";
        r[idKey] = config.generarId([...todos, ...nuevos.slice(0, i)]);
        r.id = Date.now() + i;
        return r;
      });
      const ok = storage.set(config.storage, [...existentes, ...conIds]);
      if (!ok) { alert("Error al guardar."); return; }
      subirDatos(config.storage);
      setResultado({ importados: conIds.length, errores: filasInvalidas.length, duplicados: duplicados.length });
      setFase("resultado");
      LOG.info("Importación completada", { modulo: moduloSeleccionado, importados: conIds.length });
    } catch (err) {
      LOG.error("Error en importación", err);
      alert("Error inesperado al importar.");
    }
  };
  const reiniciar = () => {
    setFase("seleccion"); setModuloSeleccionado(null);
    setArchivoNombre(""); setFilasValidas([]); setFilasInvalidas([]);
    setDuplicados([]); setResultado(null);
    if (fileRef.current) fileRef.current.value = "";
  };
  return (
    <div style={{ minHeight: "100vh", background: bg, fontFamily: "sans-serif" }}>
      {/* HEADER */}
      <div style={{ background: navBg, padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button onClick={fase === "seleccion" ? onVolver : reiniciar}
            style={{ background: "rgba(255,255,255,0.1)", border: "none", color: "white", padding: "6px 14px", borderRadius: "10px", cursor: "pointer", fontSize: "13px" }}>
            ← {fase === "seleccion" ? "Menú" : "Volver"}
          </button>
          <span style={{ color: "white", fontWeight: "700", fontSize: "16px" }}>📥 Importar datos CSV</span>
        </div>
        {usuario?.rol === "Administrador" && <BotonMapaProceso moduloKey="importar_csv" oscuro={oscuro} />}
      </div>
      <div style={{ padding: "1rem 1.5rem", maxWidth: "800px", margin: "0 auto" }}>
        {/* FASE: SELECCIÓN DE MÓDULO */}
        {fase === "seleccion" && (
          <>
            <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#F0EEFF", border: `1px solid ${oscuro ? "#7B5BF2" : "#d4c8ff"}` }}>
              <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#7B5BF2", marginBottom: "8px" }}>📋 Cómo funciona</h3>
              <p style={{ fontSize: "13px", color: subtexto, margin: "0 0 6px" }}>1. Seleccioná el módulo al que querés importar datos</p>
              <p style={{ fontSize: "13px", color: subtexto, margin: "0 0 6px" }}>2. Descargá la plantilla CSV con las columnas correctas</p>
              <p style={{ fontSize: "13px", color: subtexto, margin: "0 0 6px" }}>3. Completá la plantilla en Excel y guardá como CSV</p>
              <p style={{ fontSize: "13px", color: subtexto, margin: "0 0 6px" }}>4. Subí el archivo — vas a ver un preview antes de confirmar</p>
              <p style={{ fontSize: "13px", color: subtexto, margin: 0 }}>5. El sistema valida, detecta duplicados y vos confirmás</p>
            </div>
            <h3 style={{ fontSize: "14px", fontWeight: "600", color: texto, marginBottom: "12px" }}>¿A qué módulo querés importar?</h3>
            {Object.entries(MODULOS_CONFIG).map(([key, cfg]) => (
              <div key={key} style={{ ...card, cursor: "pointer", border: moduloSeleccionado === key ? `2px solid ${cfg.color}` : `1px solid ${borde}`, background: moduloSeleccionado === key ? `${cfg.color}11` : cardBg }}
                onClick={() => setModuloSeleccionado(key)}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <p style={{ fontSize: "15px", fontWeight: "700", color: cfg.color, margin: "0 0 4px" }}>{cfg.label}</p>
                    <p style={{ fontSize: "12px", color: subtexto, margin: 0 }}>{cfg.campos.length} campos · {cfg.campos.filter(c => c.requerido).length} requeridos</p>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <button onClick={(e) => { e.stopPropagation(); descargarPlantilla(key); }}
                      style={{ ...btn(cfg.color), fontSize: "11px", padding: "6px 12px" }}>
                      ⬇️ Plantilla
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {moduloSeleccionado && (
              <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#f0fff4", border: `1px solid ${config.color}` }}>
                <h3 style={{ fontSize: "13px", fontWeight: "600", color: config.color, marginBottom: "10px" }}>
                  Subir archivo CSV para {config.label}
                </h3>
                <p style={{ fontSize: "12px", color: subtexto, marginBottom: "12px" }}>
                  El archivo debe tener las mismas columnas que la plantilla descargada.
                </p>
                <input ref={fileRef} type="file" accept=".csv" onChange={leerArchivo}
                  style={{ display: "none" }} />
                <button onClick={() => fileRef.current?.click()}
                  style={{ ...btn(config.color), width: "100%", padding: "14px", fontSize: "14px" }}>
                  📂 Seleccionar archivo CSV
                </button>
                {archivoNombre && <p style={{ fontSize: "12px", color: "#22c55e", marginTop: "8px", textAlign: "center" }}>✅ {archivoNombre}</p>}
              </div>
            )}
          </>
        )}
        {/* FASE: PREVIEW */}
        {fase === "preview" && config && (
          <>
            <div style={{ ...card, background: oscuro ? "#0a1f5c" : "#f0f8ff", border: `1px solid ${oscuro ? "#1e3a8a" : "#bde0fe"}` }}>
              <h3 style={{ fontSize: "14px", fontWeight: "600", color: "#3b82f6", marginBottom: "12px" }}>
                📊 Resumen del archivo: {archivoNombre}
              </h3>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px" }}>
                {[["✅ Válidos", filasValidas.length, "#22c55e"], ["🔴 Con errores", filasInvalidas.length, "#e63946"], ["🔁 Duplicados", duplicados.length, "#f59e0b"]].map(([lab, val, col]) => (
                  <div key={lab} style={{ background: cardBg, borderRadius: "10px", padding: "12px", textAlign: "center", border: `1px solid ${borde}` }}>
                    <p style={{ fontSize: "22px", fontWeight: "700", color: col, margin: 0 }}>{val}</p>
                    <p style={{ fontSize: "11px", color: subtexto, margin: "4px 0 0" }}>{lab}</p>
                  </div>
                ))}
              </div>
            </div>
            {filasInvalidas.length > 0 && (
              <div style={{ ...card, background: oscuro ? "#1a0505" : "#fff5f5", border: "1px solid #e63946" }}>
                <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#e63946", marginBottom: "10px" }}>
                  🔴 Filas con errores (no se importarán)
                </h3>
                {filasInvalidas.slice(0, 5).map((item, i) => (
                  <div key={i} style={{ marginBottom: "8px", padding: "8px", background: oscuro ? "#0d2160" : "#fff", borderRadius: "8px" }}>
                    {item.errores.map((e, j) => (
                      <p key={j} style={{ fontSize: "12px", color: "#e63946", margin: "2px 0" }}>• {e}</p>
                    ))}
                  </div>
                ))}
                {filasInvalidas.length > 5 && <p style={{ fontSize: "11px", color: subtexto }}>...y {filasInvalidas.length - 5} más</p>}
              </div>
            )}
            {duplicados.length > 0 && (
              <div style={{ ...card, background: oscuro ? "#1a1000" : "#fffbeb", border: "1px solid #f59e0b" }}>
                <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#f59e0b", marginBottom: "8px" }}>
                  🔁 Duplicados detectados (no se importarán)
                </h3>
                <p style={{ fontSize: "12px", color: subtexto, margin: 0 }}>
                  {duplicados.length} registros ya existen en el sistema y serán omitidos.
                </p>
              </div>
            )}
            {filasValidas.length > 0 && (
              <div style={{ ...card, background: oscuro ? "#0a1f5c" : "white", border: `1px solid ${borde}` }}>
                <h3 style={{ fontSize: "13px", fontWeight: "600", color: "#22c55e", marginBottom: "10px" }}>
                  ✅ Preview — primeros registros válidos
                </h3>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px" }}>
                    <thead>
                      <tr>
                        {config.campos.slice(0, 5).map(c => (
                          <th key={c.key} style={{ background: "#09184D", color: "white", padding: "6px 8px", textAlign: "left" }}>{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filasValidas.slice(0, 5).map((fila, i) => (
                        <tr key={i} style={{ background: i % 2 === 0 ? (oscuro ? "#091840" : "#f8f9fa") : cardBg }}>
                          {config.campos.slice(0, 5).map(c => (
                            <td key={c.key} style={{ padding: "5px 8px", color: texto, borderBottom: `0.5px solid ${borde}` }}>
                              {fila[c.label] || fila[c.key] || "—"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filasValidas.length > 5 && <p style={{ fontSize: "11px", color: subtexto, marginTop: "8px" }}>...y {filasValidas.length - 5} registros más</p>}
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: "10px", marginTop: "1rem" }}>
              <button onClick={reiniciar} style={{ ...btn("#808085"), flex: 1 }}>Cancelar</button>
              {filasValidas.length > 0
                ? <button onClick={confirmarImportacion} style={{ ...btn(config.color), flex: 2 }}>
                    ✅ Confirmar importación de {filasValidas.length} registro{filasValidas.length !== 1 ? "s" : ""}
                  </button>
                : <button disabled style={{ ...btn("#ccc"), flex: 2, cursor: "not-allowed" }}>Sin registros válidos</button>
              }
            </div>
          </>
        )}
        {/* FASE: RESULTADO */}
        {fase === "resultado" && resultado && (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <p style={{ fontSize: "60px", marginBottom: "1rem" }}>🎉</p>
            <h2 style={{ fontSize: "22px", fontWeight: "700", color: config.color, marginBottom: "8px" }}>
              ¡Importación completada!
            </h2>
            <p style={{ fontSize: "15px", color: texto, marginBottom: "1.5rem" }}>
              {resultado.importados} registro{resultado.importados !== 1 ? "s" : ""} importado{resultado.importados !== 1 ? "s" : ""} correctamente en {config.label}
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "10px", maxWidth: "400px", margin: "0 auto 2rem" }}>
              {[["✅ Importados", resultado.importados, "#22c55e"], ["🔴 Errores", resultado.errores, "#e63946"], ["🔁 Duplicados", resultado.duplicados, "#f59e0b"]].map(([lab, val, col]) => (
                <div key={lab} style={{ background: cardBg, borderRadius: "12px", padding: "1rem", border: `1px solid ${borde}`, textAlign: "center" }}>
                  <p style={{ fontSize: "20px", fontWeight: "700", color: col, margin: 0 }}>{val}</p>
                  <p style={{ fontSize: "10px", color: subtexto, margin: "4px 0 0" }}>{lab}</p>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              <button onClick={reiniciar} style={btn("#7B5BF2")}>Importar otro archivo</button>
              <button onClick={onVolver} style={btn("#09184D")}>Volver al menú</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
